/**
 * Libro Mayor - Finance 360
 * General ledger view with account movements
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import {
  BookOpen,
  Search,
  Filter,
  ChevronLeft,
  ChevronRight,
  ArrowUpRight,
  ArrowDownRight,
  Calendar,
  RefreshCw,
  Download,
  FileSpreadsheet,
  FileText
} from 'lucide-react';
import toast from 'react-hot-toast';
import { contabilidadApi } from '../../utils/api';
import useAuthStore from '../../store/authStore';
import LoadingSpinner, { LoadingPage } from '../../components/LoadingSpinner';
import MonthRangePicker from '../../components/MonthRangePicker';

import useFiltroPeriodo from '../../hooks/useFiltroPeriodo';
import usePeriodosContables from '../../hooks/usePeriodosContables';
// Toolbar button component
function ToolbarButton({ icon: Icon, label, onClick, disabled, variant = 'default' }) {
  const baseClasses = "flex flex-col items-center justify-center px-3 py-2 rounded-lg transition-all text-xs font-medium min-w-[70px]";
  const variants = {
    default: "border border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400",
    primary: "border border-blue-400 text-blue-600 hover:bg-blue-50 hover:border-blue-500",
  };

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`${baseClasses} ${variants[variant]} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
    >
      <Icon className="h-5 w-5 mb-1" />
      <span className="whitespace-nowrap">{label}</span>
    </button>
  );
}

function ToolbarSeparator() {
  return <div className="w-px h-12 bg-gray-300 mx-1" />;
}

export default function LibroMayor() {
  const { cuentaId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentEmisor } = useAuthStore();

  const [loading, setLoading] = useState(true);
  const [cuentas, setCuentas] = useState([]);
  const [selectedCuenta, setSelectedCuenta] = useState(null);
  const [movimientos, setMovimientos] = useState([]);
  const [resumen, setResumen] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });

  // Filters
  const [search, setSearch] = useState('');
  // Filtro de período unificado. Default: vacío (todo el tiempo).
  // Los períodos contables (mensuales o de 4 semanas) también son filtro (S2-02).
  const periodos = usePeriodosContables();
  const [filtroFecha, setFiltroFecha] = useFiltroPeriodo('LibroMayor', { modo: 'rango', desde: '', hasta: '' });
  // Aliases para no romper exports/llamadas API existentes
  const fechaDesde = filtroFecha.desde;
  const fechaHasta = filtroFecha.hasta;
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [exporting, setExporting] = useState(false);
  const exportMenuRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(event.target)) {
        setShowExportMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (currentEmisor) {
      loadCuentas();
    }
  }, [currentEmisor]);

  useEffect(() => {
    if (cuentaId && cuentas.length > 0) {
      const cuenta = cuentas.find(c => c.id === parseInt(cuentaId));
      setSelectedCuenta(cuenta || null);
      loadMovimientos(cuentaId);
    } else if (cuentaId && cuentas.length === 0) {
      // Load movimientos even if cuenta not in list (might have movements but not in resumen)
      loadMovimientos(cuentaId);
    }
  }, [cuentaId, cuentas]);

  // Al cambiar el filtro se recarga con las fechas NUEVAS. Antes el onChange
  // llamaba a loadMovimientos en el mismo instante y este leía las de antes:
  // la tabla mostraba el rango anterior al elegido (S2-02).
  const primeraCarga = useRef(true);
  useEffect(() => {
    if (primeraCarga.current) { primeraCarga.current = false; return; }
    if (cuentaId) loadMovimientos(cuentaId);
  }, [fechaDesde, fechaHasta]);

  const loadCuentas = async () => {
    try {
      const response = await contabilidadApi.getLibroMayorResumen();
      setCuentas(response.data.data);
    } catch (error) {
      console.error('Error loading cuentas:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMovimientos = async (id, page = 1) => {
    setLoading(true);
    try {
      const response = await contabilidadApi.getLibroMayor(id, {
        page,
        limit: 30,
        fechaDesde: fechaDesde || undefined,
        fechaHasta: fechaHasta || undefined
      });
      const data = response.data.data;
      // Backend returns: { cuenta, saldoInicial, movimientos, saldoFinal, pagination }
      setMovimientos(data?.movimientos || []);
      setResumen({
        saldoInicial: data?.saldoInicial || 0,
        saldoFinal: data?.saldoFinal || 0,
        totalDebitos: (data?.movimientos || []).reduce((sum, m) => sum + (m.debito || 0), 0),
        totalCreditos: (data?.movimientos || []).reduce((sum, m) => sum + (m.credito || 0), 0)
      });
      setPagination(data?.pagination || { page: 1, pages: 1, total: 0 });
      // Also set selectedCuenta from response if available
      if (data?.cuenta) {
        setSelectedCuenta(data.cuenta);
      }
    } catch (error) {
      console.error('Error loading movimientos:', error);
      setMovimientos([]);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-CR', {
      style: 'currency',
      currency: 'CRC',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const filteredCuentas = cuentas.filter(c =>
    (c.codigo || '').toLowerCase().includes(search.toLowerCase()) ||
    (c.nombre || '').toLowerCase().includes(search.toLowerCase())
  );

  const handleExport = async (format) => {
    setShowExportMenu(false);
    if (!cuentaId) return;

    setExporting(true);
    try {
      const response = await contabilidadApi.exportLibroMayor(cuentaId, {
        fechaDesde: fechaDesde || undefined,
        fechaHasta: fechaHasta || undefined,
        format
      });

      const blob = new Blob([response.data], {
        type: format === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const cuenta = selectedCuenta?.codigo || cuentaId;
      const fileName = `Libro_Mayor_${cuenta}${fechaDesde ? `_${fechaDesde}` : ''}${fechaHasta ? `_${fechaHasta}` : ''}.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Libro Mayor exportado a ${format.toUpperCase()}`);
    } catch (error) {
      console.error('Error exporting:', error);
      toast.error('Error al exportar el reporte');
    } finally {
      setExporting(false);
    }
  };

  if (loading && !selectedCuenta) {
    return <LoadingPage />;
  }

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="bg-white rounded-lg border p-3 flex items-center justify-center gap-2 flex-wrap">
        <ToolbarButton
          icon={RefreshCw}
          label="Actualizar"
          onClick={() => cuentaId ? loadMovimientos(cuentaId) : loadCuentas()}
          disabled={loading}
        />

        <ToolbarSeparator />

        <div className="relative" ref={exportMenuRef}>
          <ToolbarButton
            icon={Download}
            label="Exportar"
            onClick={() => setShowExportMenu(!showExportMenu)}
            disabled={exporting || !cuentaId}
          />
          {showExportMenu && (
            <div className="absolute top-full left-0 mt-1 bg-white border rounded-lg shadow-lg z-10 py-1 min-w-[170px] w-max">
              <button
                onClick={() => handleExport('pdf')}
                className="w-full flex items-center gap-2 px-4 py-2 text-sm whitespace-nowrap text-gray-700 hover:bg-gray-100"
              >
                <FileText className="h-4 w-4 text-red-500" />
                Exportar PDF
              </button>
              <button
                onClick={() => handleExport('excel')}
                className="w-full flex items-center gap-2 px-4 py-2 text-sm whitespace-nowrap text-gray-700 hover:bg-gray-100"
              >
                <FileSpreadsheet className="h-4 w-4 text-green-500" />
                Exportar Excel
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        {/* Account List */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl border">
            <div className="p-4 border-b">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Buscar cuenta..."
                  className="w-full pl-10 pr-4 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="max-h-[calc(var(--alto-contenido)-80px)] overflow-y-auto">
              {filteredCuentas.length === 0 ? (
                <div className="p-4 text-center text-gray-500 text-sm">
                  No hay cuentas con movimiento
                </div>
              ) : (
                <div className="divide-y">
                  {filteredCuentas.map((cuenta) => (
                    <Link
                      key={cuenta.id}
                      to={`/contabilidad/mayor/${cuenta.id}`}
                      className={`block p-3 hover:bg-gray-50 ${
                        selectedCuenta?.id === cuenta.id ? 'bg-blue-50 border-l-2 border-blue-500' : ''
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-gray-900 text-sm">{cuenta.codigo}</p>
                          <p className="text-xs text-gray-500 line-clamp-1">{cuenta.nombre}</p>
                        </div>
                        <div className="text-right">
                          <p className={`text-sm font-medium ${
                            cuenta.saldoFinal >= 0 ? 'text-green-600' : 'text-red-600'
                          }`}>
                            {formatCurrency(Math.abs(cuenta.saldoFinal))}
                          </p>
                          <p className="text-xs text-gray-400">{cuenta.movimientos} mov.</p>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Movements Detail */}
        <div className="lg:col-span-3">
          {!cuentaId ? (
            <div className="bg-white rounded-xl border p-8 text-center">
              <BookOpen className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <h3 className="font-medium text-gray-900">Seleccione una cuenta</h3>
              <p className="text-sm text-gray-500 mt-1">
                Elija una cuenta de la lista para ver sus movimientos
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Account Summary */}
              {resumen && (
                <div className="bg-white rounded-xl border p-4">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="text-center p-3 bg-gray-50 rounded-lg">
                      <p className="text-xs text-gray-500">Saldo Inicial</p>
                      <p className="text-lg font-bold text-gray-900">
                        {formatCurrency(resumen.saldoInicial)}
                      </p>
                    </div>
                    <div className="text-center p-3 bg-blue-50 rounded-lg">
                      <p className="text-xs text-gray-500">Total Débitos</p>
                      <p className="text-lg font-bold text-blue-600">
                        {formatCurrency(resumen.totalDebitos)}
                      </p>
                    </div>
                    <div className="text-center p-3 bg-purple-50 rounded-lg">
                      <p className="text-xs text-gray-500">Total Créditos</p>
                      <p className="text-lg font-bold text-purple-600">
                        {formatCurrency(resumen.totalCreditos)}
                      </p>
                    </div>
                    <div className="text-center p-3 bg-green-50 rounded-lg">
                      <p className="text-xs text-gray-500">Saldo Final</p>
                      <p className={`text-lg font-bold ${
                        resumen.saldoFinal >= 0 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {formatCurrency(resumen.saldoFinal)}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Filtros */}
              <div className="flex items-center gap-4 bg-white p-4 rounded-xl border">
                <MonthRangePicker
                  value={filtroFecha}
                  onChange={setFiltroFecha}
                  periodos={periodos}
                />
              </div>

              {/* Movements Table */}
              <div className="bg-white rounded-xl border overflow-hidden">
                {loading ? (
                  <div className="p-8 flex justify-center">
                    <LoadingSpinner />
                  </div>
                ) : !movimientos || movimientos.length === 0 ? (
                  <div className="p-8 text-center text-gray-500">
                    No hay movimientos en el período seleccionado
                  </div>
                ) : (
                  <>
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Fecha
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Asiento / Concepto
                          </th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                            Débito
                          </th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                            Crédito
                          </th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                            Saldo
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {(movimientos || []).map((mov, index) => (
                          <tr key={mov.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm text-gray-600">
                              {new Date(mov.fecha).toLocaleDateString('es-CR')}
                            </td>
                            <td className="px-4 py-3">
                              <Link
                                to={`/contabilidad/asientos/${mov.asientoId}`}
                                className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                              >
                                #{mov.asientoNumero}
                              </Link>
                              <p className="text-xs text-gray-500 line-clamp-1">
                                {mov.descripcion || mov.concepto}
                              </p>
                            </td>
                            <td className="px-4 py-3 text-right">
                              {mov.debito > 0 && (
                                <span className="text-sm font-medium text-gray-900">
                                  {formatCurrency(mov.debito)}
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-right">
                              {mov.credito > 0 && (
                                <span className="text-sm font-medium text-gray-900">
                                  {formatCurrency(mov.credito)}
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-right">
                              <span className={`text-sm font-medium ${
                                mov.saldo >= 0 ? 'text-green-600' : 'text-red-600'
                              }`}>
                                {formatCurrency(mov.saldo)}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    {/* Pagination */}
                    {pagination.pages > 1 && (
                      <div className="flex items-center justify-between px-4 py-3 border-t">
                        <p className="text-sm text-gray-500">
                          Mostrando {movimientos.length} de {pagination.total}
                        </p>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => loadMovimientos(cuentaId, pagination.page - 1)}
                            disabled={pagination.page === 1}
                            className="p-2 text-gray-500 hover:bg-gray-100 rounded disabled:opacity-50"
                          >
                            <ChevronLeft className="h-4 w-4" />
                          </button>
                          <span className="text-sm text-gray-700">
                            {pagination.page} / {pagination.pages}
                          </span>
                          <button
                            onClick={() => loadMovimientos(cuentaId, pagination.page + 1)}
                            disabled={pagination.page === pagination.pages}
                            className="p-2 text-gray-500 hover:bg-gray-100 rounded disabled:opacity-50"
                          >
                            <ChevronRight className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
